/**
 * importImageFailures — the bulk-import run's record of every image it could
 * not get, so a reference never commits imageless without the curator hearing
 * about it.
 *
 * Why it exists: the 2026-10-19 Panerai import created 40 references with no
 * image at all. Panerai (Akamai) refuses every image fetch from a cloud IP, the
 * failures were console.warn'd, and the run summary said "40 added". Each
 * failure is now collected here, per reference, and the done step shows the
 * count, the per-row reason and a retry.
 *
 * Pure (no Firebase), so the bookkeeping is testable on its own. The entry type
 * is a parameter only to keep this file free of BulkImport's local types — the
 * retry needs the scraped entry to rebuild the gallery from.
 */

export type ImportImageKind = 'main' | 'gallery';

export interface ImportImageFailure {
  kind: ImportImageKind;
  url: string;
  error: string;
}

export interface ImportImageFailureRow<E = unknown> {
  brandId: string;
  referenceId: string;
  /** What the curator recognises the row by — the ref string. */
  reference: string;
  /** The scraped entry the reference was written from; the retry rebuilds from it. */
  entry: E;
  failures: ImportImageFailure[];
}

export interface ImportImageFailureSummary {
  /** References with at least one failed image. */
  refs: number;
  /** Every failed image fetch, hero and gallery. */
  images: number;
  /** References whose HERO failed — the ones that may have no image at all. */
  main: number;
  gallery: number;
}

function rowKey(brandId: string, referenceId: string): string {
  return `${brandId}/${referenceId}`;
}

/**
 * One run's failures, keyed by reference. The same reference can be noted more
 * than once (hero, then each gallery shot); the first note names the row.
 */
export class ImportImageFailureCollector<E = unknown> {
  private readonly byRef = new Map<string, ImportImageFailureRow<E>>();

  note(
    target: { brandId: string; referenceId: string; reference: string; entry: E },
    failure: ImportImageFailure,
  ): void {
    const key = rowKey(target.brandId, target.referenceId);
    let row = this.byRef.get(key);
    if (!row) {
      row = { ...target, failures: [] };
      this.byRef.set(key, row);
    }
    // A re-run of the same image (the parent pre-pass and a variant can share a
    // template) is one failure, not two.
    if (row.failures.some((f) => f.kind === failure.kind && f.url === failure.url)) return;
    row.failures.push(failure);
  }

  rows(): ImportImageFailureRow<E>[] {
    return [...this.byRef.values()].map((row) => ({ ...row, failures: [...row.failures] }));
  }
}

export function summarizeImageFailures(rows: readonly ImportImageFailureRow[]): ImportImageFailureSummary {
  let images = 0;
  let main = 0;
  let gallery = 0;
  let refs = 0;
  for (const row of rows) {
    if (row.failures.length === 0) continue;
    refs += 1;
    images += row.failures.length;
    if (row.failures.some((f) => f.kind === 'main')) main += 1;
    gallery += row.failures.filter((f) => f.kind === 'gallery').length;
  }
  return { refs, images, main, gallery };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The headline the done step leads with, e.g. "40 refs committed, 40 image fetches failed". */
export function formatImageFailureHeadline(
  committed: number,
  summary: ImportImageFailureSummary,
): string {
  return `${plural(committed, 'ref', 'refs')} committed, ${plural(summary.images, 'image fetch', 'image fetches')} failed`;
}

/**
 * Replace a row's failures with what is still failing after a retry; rows left
 * with nothing are dropped.
 */
export function applyRetryOutcome<E>(
  rows: readonly ImportImageFailureRow<E>[],
  brandId: string,
  referenceId: string,
  remaining: ImportImageFailure[],
): ImportImageFailureRow<E>[] {
  return rows.flatMap((row) => {
    if (row.brandId !== brandId || row.referenceId !== referenceId) return [row];
    return remaining.length > 0 ? [{ ...row, failures: remaining }] : [];
  });
}
